import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CalAngularService } from '@cvx/cal-angular';
import { map, Observable, switchMap, tap } from 'rxjs';
import { SkipApiInterceptorToken } from '../http/api-interceptor.service';
import { CalConfig } from './cal-config.model';
import { User } from './models/user.model';
import { UserGraph } from '../common/models/user-graph.model';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    public constructor(
        private readonly httpClient: HttpClient,
        private readonly authService: CalAngularService,
    ) {}

    public getUserName(): Observable<User> {
        return this.authService.isUserSignedIn().pipe(
            map((isUserSignedIn) => {
                if (!isUserSignedIn) {
                    throw new Error("Can't get the user's name because they aren't signed in");
                }

                return this.authService.cvxClaimsPrincipal as User;
            }),
        );
    }

    public getAllUsers(textToFilter: string): Observable<UserGraph[]> {
        return this.getGraphToken().pipe(
            switchMap((token) => {
                const url = `https://graph.microsoft.com/v1.0/users?$search="displayName:${textToFilter}"`;
                return this.httpClient
                    .get<{ value: UserGraph[] }>(url, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            ConsistencyLevel: 'eventual',
                        },
                        context: new HttpContext().set(SkipApiInterceptorToken, { skipApiInterceptor: true }),
                    })
                    .pipe(
                        map((response) => response.value),
                        tap((response) => {
                            console.log(response);
                        }),
                    );
            }),
        );
    }

    public getGraphToken(): Observable<string> {
        const scopes = ['https://graph.microsoft.com/User.Read.All'];
        const fullAuthResponse = true;
        return this.authService.getAADToken(scopes, fullAuthResponse).pipe(
            map((authResult) => {
                if (!authResult) {
                    throw new TypeError("getAADToken() shouldn't return null");
                }

                if (typeof authResult === 'string') {
                    return authResult;
                }

                return authResult.accessToken;
            }),
        );
    }

    public getAuthToken(): Observable<string> {
        return this.getCalConfig().pipe(
            switchMap((config) => {
                const scopes = [`${config.clientId}/user_impersonation`];
                const fullAuthResponse = true;
                return this.authService.getAADToken(scopes, fullAuthResponse).pipe(
                    map((authResult) => {
                        if (!authResult) {
                            throw new TypeError("getAADToken() shouldn't return null");
                        }

                        if (typeof authResult === 'string') {
                            return authResult;
                        }

                        return authResult.accessToken;
                    }),
                );
            }),
        );
    }

    private getCalConfig(): Observable<CalConfig> {
        return this.httpClient.get<CalConfig>('assets/cal-config.json', {
            context: new HttpContext().set(SkipApiInterceptorToken, { skipApiInterceptor: true }),
        });
    }
}
